import ExcelJS from 'exceljs';
import { getSalesPriceList, bulkUpdatePosProducts } from '../services/salesService';
import { cellText, parseActiveCell } from './classificationExcel';

export const POS_CONFIG_COL = {
  id: 'ID',
  sku: 'SKU (referencia)',
  name: 'Nombre',
  unit: 'Unidad',
  enabled: 'Vende en este POS (Sí/No)',
};

const HEADER_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFD1FAE5' },
};

function downloadBuffer(buffer, filename) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function styleHeaderRow(row, labels, fill = HEADER_FILL) {
  labels.forEach((label, i) => {
    const c = row.getCell(i + 1);
    c.value = label;
    c.font = { bold: true };
    c.fill = fill;
  });
}

function boolLabel(value) {
  return value ? 'Sí' : 'No';
}

export function buildPosConfigHeaders() {
  return [
    POS_CONFIG_COL.id,
    POS_CONFIG_COL.sku,
    POS_CONFIG_COL.name,
    POS_CONFIG_COL.unit,
    POS_CONFIG_COL.enabled,
  ];
}

function productToRow(product, headers) {
  const row = {
    [POS_CONFIG_COL.id]: product.id,
    [POS_CONFIG_COL.sku]: product.sku || '',
    [POS_CONFIG_COL.name]: product.name || '',
    [POS_CONFIG_COL.unit]: product.unit_abbreviation || product.unit_of_measure || '',
    [POS_CONFIG_COL.enabled]: boolLabel(product.is_enabled_for_pos === true),
  };
  return headers.map((h) => row[h] ?? '');
}

export async function fetchAllPosConfigProducts(pointOfSaleId) {
  const all = [];
  let page = 1;
  let lastPage = 1;
  do {
    const res = await getSalesPriceList(Number(pointOfSaleId), '', page, 200, 'config');
    if (!res?.success) {
      throw new Error(res?.message || 'No se pudo cargar el catálogo del punto de venta.');
    }
    all.push(...(res.data || []));
    lastPage = res.meta?.last_page ?? 1;
    page += 1;
  } while (page <= lastPage);
  return all;
}

export async function downloadPosConfigSheet(products = [], posLabel = '') {
  const headers = buildPosConfigHeaders();
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Configuración POS', { views: [{ state: 'frozen', ySplit: 3 }] });
  const lastCol = String.fromCharCode('A'.charCodeAt(0) + headers.length - 1);

  ws.mergeCells(`A1:${lastCol}1`);
  ws.getCell('A1').value =
    'Configuración POS: indique Sí o No en «Vende en este POS». No modifique ID ni SKU. Edite solo las filas necesarias y vuelva a importar.';
  ws.getCell('A1').alignment = { wrapText: true, vertical: 'middle' };
  ws.getRow(1).height = 48;

  ws.mergeCells(`A2:${lastCol}2`);
  ws.getCell('A2').value = `${products.length} producto(s)${posLabel ? ` · ${posLabel}` : ''}`;
  ws.getCell('A2').font = { italic: true, color: { argb: 'FF4B5563' } };

  styleHeaderRow(ws.getRow(3), headers);

  const sorted = [...products].sort((a, b) =>
    String(a?.sku || '').localeCompare(String(b?.sku || ''), 'es', { sensitivity: 'base' }),
  );
  sorted.forEach((product) => {
    ws.addRow(productToRow(product, headers));
  });

  ws.columns = [
    { width: 8 },
    { width: 22 },
    { width: 36 },
    { width: 10 },
    { width: 24 },
  ];

  const buf = await wb.xlsx.writeBuffer();
  const date = new Date().toISOString().split('T')[0];
  const safePos = String(posLabel || 'pos')
    .replace(/[^\w\-]+/g, '_')
    .slice(0, 40);
  downloadBuffer(buf, `configuracion_pos_${safePos}_${date}.xlsx`);
}

function headerMapFromRow(row) {
  const map = {};
  row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const t = cellText(cell);
    if (t) map[t] = colNumber;
  });
  return map;
}

function findHeaderRow(sheet, requiredLabels) {
  const maxScan = Math.min(30, sheet.rowCount || 30);
  for (let r = 1; r <= maxScan; r++) {
    const row = sheet.getRow(r);
    const map = headerMapFromRow(row);
    if (requiredLabels.every((label) => map[label] != null)) {
      return { rowIndex: r, colMap: map };
    }
  }
  return null;
}

function getLastUsedRow(sheet) {
  let last = 0;
  sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber > last) last = rowNumber;
  });
  return last;
}

async function loadFirstSheet(file) {
  const buf = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  return wb.worksheets[0] || null;
}

const REQUIRED_HEADERS = [POS_CONFIG_COL.id, POS_CONFIG_COL.enabled];

export async function analyzePosConfigImport(file, { pointOfSaleId }) {
  const sheet = await loadFirstSheet(file);
  if (!sheet) {
    return { success: false, fatalMessage: 'El archivo no tiene hojas.' };
  }

  const found = findHeaderRow(sheet, REQUIRED_HEADERS);
  if (!found) {
    return {
      success: false,
      fatalMessage: `Faltan encabezados obligatorios. Debe existir al menos: ${REQUIRED_HEADERS.join(', ')}.`,
    };
  }

  let products = [];
  try {
    products = await fetchAllPosConfigProducts(pointOfSaleId);
  } catch (err) {
    return { success: false, fatalMessage: err?.message || 'No se pudieron cargar los productos del POS.' };
  }

  const productById = new Map(products.map((p) => [Number(p.id), p]));
  const { rowIndex: headerRow, colMap } = found;
  const last = getLastUsedRow(sheet);
  const previewRows = [];
  const validTasks = [];

  for (let r = headerRow + 1; r <= last; r++) {
    const row = sheet.getRow(r);
    const idRaw = cellText(row.getCell(colMap[POS_CONFIG_COL.id]));
    const skuRaw = colMap[POS_CONFIG_COL.sku] ? cellText(row.getCell(colMap[POS_CONFIG_COL.sku])) : '';
    const nameRaw = colMap[POS_CONFIG_COL.name] ? cellText(row.getCell(colMap[POS_CONFIG_COL.name])) : '';
    const unitRaw = colMap[POS_CONFIG_COL.unit] ? cellText(row.getCell(colMap[POS_CONFIG_COL.unit])) : '';
    const enabledRaw = cellText(row.getCell(colMap[POS_CONFIG_COL.enabled]));

    if (!idRaw && !skuRaw && !nameRaw && !enabledRaw) {
      previewRows.push({ excelRow: r, status: 'empty', cols: {}, issue: '' });
      continue;
    }

    const colsPreview = {
      id: idRaw,
      sku: skuRaw,
      nombre: nameRaw,
      unidad: unitRaw,
      vende: enabledRaw,
    };

    const id = parseInt(String(idRaw).trim(), 10);
    if (!Number.isFinite(id) || id < 1) {
      previewRows.push({
        excelRow: r,
        status: 'invalid',
        cols: colsPreview,
        issue: 'ID de producto inválido.',
      });
      continue;
    }

    const existing = productById.get(id);
    if (!existing) {
      previewRows.push({
        excelRow: r,
        status: 'invalid',
        cols: colsPreview,
        issue: `El producto ID ${id} no está marcado como vendible en el catálogo.`,
      });
      continue;
    }

    if (enabledRaw === '') {
      previewRows.push({
        excelRow: r,
        status: 'unchanged',
        cols: {
          ...colsPreview,
          sku: existing.sku || skuRaw,
          nombre: existing.name || nameRaw,
        },
        issue: 'Sin valor en «Vende en este POS»; se omite.',
      });
      continue;
    }

    const enabled = parseActiveCell(enabledRaw);
    if (enabled === Boolean(existing.is_enabled_for_pos)) {
      previewRows.push({
        excelRow: r,
        status: 'unchanged',
        cols: {
          ...colsPreview,
          sku: existing.sku || skuRaw,
          nombre: existing.name || nameRaw,
          vende: boolLabel(enabled),
        },
        issue: 'Sin cambios respecto al sistema.',
      });
      continue;
    }

    previewRows.push({
      excelRow: r,
      status: 'valid',
      cols: {
        ...colsPreview,
        sku: existing.sku || skuRaw,
        nombre: existing.name || nameRaw,
        vende: boolLabel(enabled),
        cambios: enabled ? 'habilitar' : 'deshabilitar',
      },
      issue: '',
    });

    validTasks.push({
      excelRow: r,
      payload: {
        product_id: id,
        is_enabled_for_pos: enabled,
      },
    });
  }

  return {
    success: true,
    previewRows,
    validTasks,
    validCount: validTasks.length,
    emptyCount: previewRows.filter((x) => x.status === 'empty').length,
    invalidCount: previewRows.filter((x) => x.status === 'invalid').length,
    unchangedCount: previewRows.filter((x) => x.status === 'unchanged').length,
  };
}

export async function executePosConfigImport(validTasks, pointOfSaleId) {
  const items = validTasks.map(({ payload }) => payload);
  if (!items.length) {
    return { updated: 0, errors: [] };
  }

  try {
    const res = await bulkUpdatePosProducts({
      point_of_sale_id: Number(pointOfSaleId),
      items,
    });
    if (!res?.success) {
      return {
        updated: 0,
        errors: (res.errors || []).map((e) => ({
          row: e.row ?? e.product_id,
          message: e.message || res.message,
        })),
      };
    }
    return {
      updated: res.data?.updated ?? items.length,
      errors: (res.errors || []).map((e) => ({
        row: e.row ?? e.product_id,
        message: e.message,
      })),
    };
  } catch (err) {
    const d = err.response?.data;
    const message = d?.message || err.message || 'Error al guardar';
    return {
      updated: 0,
      errors: [{ row: '-', message }],
    };
  }
}

export function summarizePosConfigResult(result) {
  const parts = [];
  if (result.updated > 0) parts.push(`${result.updated} actualizado(s)`);
  if (result.errors?.length) parts.push(`${result.errors.length} error(es)`);
  if (!parts.length) return 'No se realizaron cambios.';
  let msg = parts.join(' · ');
  if (result.errors?.length) {
    const detail = result.errors
      .slice(0, 5)
      .map((e) => `Fila ${e.row}: ${e.message}`)
      .join(' | ');
    msg += `. ${detail}`;
  }
  return msg;
}
